import { decamelizeKeys } from 'humps'
import { Feedback, SentFeedback } from 'src/models/Feedback'
import { RoomType } from 'src/models/Room'
import { PromisedType } from 'src/utils/types'
import useSWR, { SWRConfiguration } from 'swr'
import { ApiClient, fetcher } from './client'
import {
  getFeedbacks,
  getSentFeedbacks,
  POST_FEEDBACK,
  POST_PENDING_FEEDBACK,
} from './endpoints'
import {
  FeedbackData,
  SentFeedbackListData,
  ServerFeedback,
  ServerSentFeedback,
} from './types/feedback'

const feedbacksFetcher = async (url: string) => {
  const res: FeedbackData = await fetcher(url)
  return {
    feedbackDetail: res.feedbackDetail,
    feedbacks: res.receivedFeedbacks.map(toFeedback),
  }
}

export const useFeedbacks = (
  config: Partial<SWRConfiguration> = {},
  page: number,
) =>
  useSWR<PromisedType<typeof feedbacksFetcher>>(
    `${getFeedbacks(page)}`,
    feedbacksFetcher,
    config,
  )

const toFeedback = (data: ServerFeedback): Feedback => ({
  id: data.id,
  content: data.content || '',
})

const sentFeedbacksFetcher = async (url: string) => {
  const res: SentFeedbackListData = await fetcher(url)
  return {
    sentFeedbacks: res.sentFeedbacks.map(toSentFeedback),
  }
}

export const useSentFeedbacks = (
  config: Partial<SWRConfiguration> = {},
  page: number,
) =>
  useSWR<PromisedType<typeof sentFeedbacksFetcher>>(
    `${getSentFeedbacks(page)}`,
    sentFeedbacksFetcher,
    config,
  )

const toSentFeedback = (data: ServerSentFeedback): SentFeedback => ({
  id: data.id,
  image: data.image?.url || '',
  companyName: data.companyName,
  adminName: data.adminName,
  position: data.position,
  sentAt: data.sentAt,
  feedbackStatus: data.feedbackStatus,
  impressionScore: data.impressionScore || null,
  content: data.content || '',
  feedbackViolationReasonTexts: data.feedbackViolationReasonTexts || [],
  className: data.className,
})

type PendingFeedbackRequest = {
  pending_feedback: {
    targetCompanyId: number
    roomId: number
    roomType: RoomType
    status: string
  }
}
export const pendingFeedback = async ({
  userId,
  ...params
}: {
  userId: number
  roomId: number
  roomType: RoomType
  status: 'pending' | 'not_interest'
}) => {
  const body: PendingFeedbackRequest = {
    pending_feedback: {
      targetCompanyId: userId,
      ...params,
    },
  }
  return ApiClient.post(POST_PENDING_FEEDBACK, decamelizeKeys(body))
}

export type FeedbackRequest = {
  feedback: {
    targetCompanyId: number
    roomId: number
    roomType: RoomType
    impressionScore: 'excellent' | 'good' | 'fair' | ''
    hasViolations: boolean
    feedbackViolationReasons: string[] | null
    content?: string
  }
}
export const feedback = async ({
  userId,
  ...params
}: {
  userId: number
  roomId: number
  roomType: RoomType
  impressionScore: 'excellent' | 'good' | 'fair' | ''
  hasViolations: boolean
  feedbackViolationReasons: string[] | null
  content?: string
}) => {
  const body: FeedbackRequest = {
    feedback: {
      targetCompanyId: userId,
      ...params,
    },
  }
  return ApiClient.post(POST_FEEDBACK, decamelizeKeys(body))
}
